import React, { useState } from "react";

import AnimateHeight from "react-animate-height";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faCircleExclamation, faCircleInfo, faShieldCheck } from "@fortawesome/pro-solid-svg-icons";

import { CountryList } from "../../../../assets/staticData/CountryList";
import { AnswerDto, AnswerListOverviewDto, KeldanKYCReport, PoliticalDanger } from "../../../../types/CompanyAnswersTypes";

import Alert from "../../../../ui-elements/Alert/Alert";
import DisplayBox from "../../../../ui-elements/DisplayBox/DisplayBox";
import Table, { IColumn } from "../../../../ui-elements/Table/Table";

import { insertDash, getTableDate, getTimeFromDate } from "../../../../services/utils";
import Link from "../../../../ui-elements/Link/Link";
import { getFileIcon } from "../../../../ui-elements/FileUploadDropZone/FileUploadDropZone";
import Tooltip from "../../../../ui-elements/Tooltip/Tooltip";
import { availableVersions, getQuestionOrder, QuestionListType, QuestionOrderMap } from "../Constants";

// Shared utilities extracted from the duplicate code in CompanyAnswers and IndividualAnswers
import { buildAnswerText, getOptionTextAnswer, hasAnyWrongAnswer } from "../shared/answerHelpers";
import SharedQuestionBlock from "../shared/QuestionBlock";


// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────
interface IProps {
    data: AnswerListOverviewDto | null;
    answerListIndex: number;
    /**
     * Controls which layout version to render:
     * - "1": Original flex-based layout with inline answer rendering (V1)
     * - "2": Grid-based layout with shared QuestionBlock, subquestion support,
     *         tooltip descriptions, and HighRiskCountries parsing (V2)
     */
    questionListVersion: availableVersions;
    questionListType: QuestionListType;
}

const CompanyAnswers: React.FC<IProps> = ({ data, answerListIndex, questionListVersion, questionListType }) => {
    const userFiles = data?.answerLists[answerListIndex].userFiles || [];
    const fileUploadError = data?.answerLists[answerListIndex].fileUploadError || false;
    const fileUploadErrorMessage = data?.answerLists[answerListIndex].fileUploadErrorMessage;
    const answers = data?.answerLists[answerListIndex].answers || [];
    const QUESTION_ORDER = getQuestionOrder(questionListVersion, questionListType);

    // ─────────────────────────────────────────────────────────────────────────
    // V1 answer renderer
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * V1 answer renderer — builds the answer cell with an optional warning icon
     * and optional extra content (e.g. PoliticalDangerTable below the answer).
     */
    const renderAnswerV1 = (isWrong: boolean, answerBoolean: boolean | null, answerText: string, content?: JSX.Element) => {
        const text = buildAnswerText(answerBoolean, answerText);
        return (
            <div className="answer-wrapper">
                <div className="answer-wrapper__text">
                    {isWrong &&
                        <FontAwesomeIcon className="fa-icon" icon={faCircleExclamation} />
                    }
                    <p className="paragraph-small--bold answer-text">
                        {text}
                    </p>
                </div>
                {content && <div className="answer-wrapper__content">
                    {content}
                </div>}
            </div>
        );
    };


    // ─────────────────────────────────────────────────────────────────────────
    // V2 answer display text
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Resolves the display text for a company answer, handling special cases
     * like country code lookups, political exposure, and high-risk countries.
     */
    const getAnswerDisplayText = (question: AnswerDto): string => {
        // Country of registration — look up the human-readable label from the code
        if (question.mainQuestionOrder === QUESTION_ORDER.COUNTRY_OF_REGISTRATION) {
            const countryLabel = CountryList.find(country => country.value === question.answertext)?.label || '';
            return buildAnswerText(question.answerBoolean, countryLabel);
        }

        // Questions with predefined options (radio/select answers)
        const optionText = getOptionTextAnswer(question);
        if (optionText !== null) return optionText;

        // Political exposure, high-risk countries, and Q8 political danger
        // display their detailed content separately, so only show the boolean here
        if (
            question.mainQuestionOrder === QUESTION_ORDER.POLITICAL_EXPOSURE ||
            question.mainQuestionOrder === QUESTION_ORDER.HIGH_RISK_COUNTRIES ||
            question.mainQuestionOrder === QUESTION_ORDER.Q8_POLITICAL_DANGER
        ) {
            return buildAnswerText(question.answerBoolean, '');
        }

        return buildAnswerText(question.answerBoolean, question.answertext);
    };


    // ─────────────────────────────────────────────────────────────────────────
    // Authentication alert
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Renders the electronic login verification alert showing who authenticated
     * and via which provider (island.is or Auðkenni).
     */
    const renderELoginAlert = () => {
        if (!data?.answerLists[answerListIndex].userELoginTimestamp) return null;

        return (
            <Alert
                type={"success"}
                iconOverWrite={faShieldCheck}
                headText={`${data?.answerLists[answerListIndex].userName}, kt. ${insertDash(data?.answerLists[answerListIndex].userSSN ?? '')}`}
                text={`Svaraði þessari könnun með rafrænni innskráningu í gegnum ${data?.answerLists[answerListIndex].islandIsLogin ? 'island.is' : 'Auðkenni'} þann
                ${getTableDate(data?.answerLists[answerListIndex].userELoginTimestamp, 'DD/MM/YYYY', '.')}
                kl. ${getTimeFromDate(data?.answerLists[answerListIndex].userELoginTimestamp, 'HH:MM:SS')} `}
                expandedText={data?.answerLists[answerListIndex].audkenniTrackId ?
                    <div>
                        <p>
                            Track ID: {data?.answerLists[answerListIndex].audkenniTrackId}<br />
                            Veitt af: Auðkenni hf.<br />
                        </p>
                    </div> : undefined
                }
            />
        );
    };


    // ─────────────────────────────────────────────────────────────────────────
    // Uploaded files section
    // ─────────────────────────────────────────────────────────────────────────

    /** Renders the uploaded files/attachments list with error handling. */
    const renderUploadedFiles = () => {
        if (fileUploadError) {
            return <div className="uploaded-file-list">
                <h5>Fylgiskjöl</h5>
                <Alert type={"error"} headText={"Villa"} text={fileUploadErrorMessage
                    ?? 'Ekki tókst að hlaða upp öllum skjölum með þessum spurningalista. Vinsamlegast hafðu samband við þennan viðskiptavin til að kalla eftir þeim skjölum sem vantar.'} />
            </div>
        }
        if (!userFiles || userFiles.length === 0) return null

        return (
            <div className="uploaded-file-list">
                <h5>Fylgiskjöl</h5>
                {userFiles.map((file, index) => (
                    <div className='uploaded-file-list__item' key={index}>
                        <div className='uploaded-file-list__item--left-section'>
                            <p className='file-item__index'>{index + 1}.</p>
                            <Link
                                url={`/DueDiligence/GetFile/${file.id}`}
                                icon={getFileIcon(file.fileExtensionType)}
                                className='uploaded-file-list__name'
                            >
                                {file.encodedFileName}
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    // ─────────────────────────────────────────────────────────────────────────
    // V2: Company-specific extra content builder
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Builds the company-specific extra content rows that get injected between
     * the description row and subquestions in the shared QuestionBlock.
     * Covers Q8 political danger, expandable registry info, and high-risk countries.
     */
    const buildV2ExtraContent = (item: AnswerDto): React.ReactNode | undefined => {
        const isQ8PoliticalDanger = item.mainQuestionOrder === QUESTION_ORDER.Q8_POLITICAL_DANGER;
        const isBeneficialOwners = item.mainQuestionOrder === QUESTION_ORDER.BENEFICIAL_OWNERS;
        const isCompanyRegistry = item.mainQuestionOrder === QUESTION_ORDER.COMPANY_REGISTRY;
        const isHighRiskCountries = item.mainQuestionOrder === QUESTION_ORDER.HIGH_RISK_COUNTRIES;
        const hasExpandableInfo = isBeneficialOwners || isCompanyRegistry;

        if (!isQ8PoliticalDanger && !hasExpandableInfo && !isHighRiskCountries) return undefined;

        const needsIndent = hasAnyWrongAnswer(item);

        return (
            <>
                {/* Q8: Political danger — shows header row and one row per person */}
                {isQ8PoliticalDanger && (
                    <>
                        <div className="question-row">
                            <div className="question-row__question">
                                <p className="paragraph-small subquestion-text">
                                    Vinsamlegast tilgreinið hvaða aðilar og hvaða störf:
                                </p>
                            </div>
                            <div className={cx("question-row__answer")}></div>
                        </div>
                        {item.politicalDanger && item.politicalDanger.length > 0 && (
                            item.politicalDanger.map((person, personIndex) => {
                                const descriptionText = person.ssn
                                    ? "Kennitala, Nafn, Háttsett starf/störf"
                                    : "Nafn, Háttsett starf/störf";

                                return (
                                    <div className="question-row" key={personIndex}>
                                        <div className="question-row__question">
                                            <p className="paragraph-small subquestion-text">
                                                {descriptionText}
                                            </p>
                                        </div>
                                        <div className={cx("question-row__answer", {
                                            "question-row__answer--indent": needsIndent,
                                            "questionIsNotWrong": !item.isWrong
                                        })}>
                                            <p className="paragraph-small--bold">
                                                {
                                                    person.ssn
                                                        ? `${insertDash(person.ssn)}, ${person.name ?? '-'}, ${person.job ?? '-'}`
                                                        : `${person.name ?? '-'}, ${person.job ?? '-'}`
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )
                            }))
                        }
                    </>
                )}

                {/* Expandable info section for beneficial owners and company registry */}
                {hasExpandableInfo && (
                    <div className="question-row">
                        <div className="question-row__question">
                            <ExpandableRegistryInfo
                                data={data?.keldanReport?.mappedReport}
                                index={item.mainQuestionOrder ?? 0}
                                questionOrder={QUESTION_ORDER}
                            />
                        </div>
                        <div className={cx("question-row__answer", {
                            "question-row__answer--indent": needsIndent,
                            "questionIsNotWrong": !item.isWrong
                        })} />
                    </div>
                )}

                {/* High-risk countries — renders parsed JSON country entries */}
                {isHighRiskCountries && (
                    <HighRiskCountriesAnswer
                        answerText={item.answertext}
                        needsIndent={needsIndent}
                    />
                )}
            </>
        );
    };


    // ─────────────────────────────────────────────────────────────────────────
    // V1 Layout: Original flex-based question/answer layout
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Renders the V1 layout with the original flex-based two-column design.
     * Uses the .answers-wrapper CSS class for a 66%/34% flex split.
     *
     * V1 does NOT support subquestions, tooltip descriptions, or the
     * HighRiskCountries JSON parsing — those are V2-only features.
     */
    const renderV1Layout = () => (
        <div className="answers-wrapper">
            <div className="answers-header">
                <h5 id="questions-overview">Spurningar</h5>
                <h5 id="answers-overview">Svör</h5>
            </div>
            {data?.answerLists[answerListIndex].answers.map((item, index) => (
                <div key={index} className='question-item'>
                    {/* Left side: question index, text, description, and expandable info */}
                    <div className="question-content">
                        <p className="paragraph-small--bold question-index">{item.order}.</p>
                        <div className="question-texts">
                            <p className="paragraph-small--bold question-title">{item.question}</p>
                            {item.questionDescription && (
                                <p className="paragraph-small--italic question-description">
                                    {item.questionDescription}
                                </p>
                            )}
                            {(item.order === QUESTION_ORDER.COMPANY_REGISTRY || item.order === QUESTION_ORDER.BENEFICIAL_OWNERS) &&
                                <ExpandableRegistryInfo data={data.keldanReport?.mappedReport} index={item.order} questionOrder={QUESTION_ORDER} />
                            }
                        </div>
                    </div>

                    {/* Right side: answer with special handling per question type */}
                    {(item.order === QUESTION_ORDER.POLITICAL_EXPOSURE)
                        ? renderAnswerV1(item.isWrong, item.answerBoolean, "", <PoliticalDangerTable data={item.politicalDanger} />)
                        : item.order === QUESTION_ORDER.COUNTRY_OF_REGISTRATION || item.order === QUESTION_ORDER.HIGH_RISK_COUNTRIES
                            ? renderAnswerV1(item.isWrong, item.answerBoolean, CountryList.find(country => country.value === item.answertext)?.label || '')
                            : renderAnswerV1(item.isWrong, item.answerBoolean, item.answertext)}
                </div>
            ))}
        </div>
    );


    // ─────────────────────────────────────────────────────────────────────────
    // V2 Layout: Grid-based layout using shared QuestionBlock
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Renders the V2 layout using the shared QuestionBlock component.
     * Company-specific features (tooltips, political danger, expandable info,
     * high-risk countries) are injected via the QuestionBlock's render props.
     */
    const renderV2Layout = () => (
        <div className="question-answer-container">
            <div className="answers-header">
                <h5 id="questions-overview">Spurningar</h5>
                <h5 id="answers-overview">Svör</h5>
            </div>

            {answers.map((item, index) => {
                // Identify which special rendering features this question needs
                const isHighRiskCountries = item.mainQuestionOrder === QUESTION_ORDER.HIGH_RISK_COUNTRIES;
                const isBusinessNature = item.mainQuestionOrder === QUESTION_ORDER.BUSINESS_NATURE;
                const isPoliticalExposure = item.mainQuestionOrder === QUESTION_ORDER.POLITICAL_EXPOSURE;
                const hasTooltipDescription = isHighRiskCountries || isBusinessNature || isPoliticalExposure;

                return (
                    <SharedQuestionBlock
                        key={index}
                        question={item}
                        index={index}
                        answerDisplayText={getAnswerDisplayText(item)}
                        showDescriptionRow={!hasTooltipDescription}
                        titleTooltip={
                            hasTooltipDescription && item.questionDescription ? (
                                <Tooltip
                                    noUnderline
                                    text={<FontAwesomeIcon icon={faCircleInfo} className="fa-icon fa-icon--info tooltip-icon" />}
                                    tooltip={item.questionDescription}
                                />
                            ) : undefined
                        }
                        descriptionSideContent={
                            isPoliticalExposure ? <PoliticalPersonList question={item} /> : undefined
                        }
                        extraContent={buildV2ExtraContent(item)}
                    />
                );
            })}
        </div>
    );


    // ─────────────────────────────────────────────────────────────────────────
    // Main render
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <DisplayBox
            id="company-answers"
            title={data?.customerName ? `Svör við áreiðanleikakönnun hjá ${data.customerName}` : "Svör við áreiðanleikakönnun"}
        >
            <div className="content">
                {renderELoginAlert()}
                {questionListVersion === "2" ? renderV2Layout() : renderV1Layout()}
                {renderUploadedFiles()}
            </div>
        </DisplayBox>
    );
};

export default CompanyAnswers;


// ═══════════════════════════════════════════════════════════════════════════════
// V1-ONLY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════


/**
 * PoliticalDangerTable — renders a table of politically exposed persons
 * for the political exposure question in the V1 layout.
 * V2 uses PoliticalPersonList (inline text) instead.
 */
interface PoliticalDangerTableProps {
    data?: PoliticalDanger[];
}

const PoliticalDangerTable: React.FC<PoliticalDangerTableProps> = ({ data }) => {
    if (!data || data.length === 0) {
        return <></>;
    }
    return (
        <Table
            tableSize="lg"
            data={data}
            columns={[{
                title: 'Háttsett starf',
                renderCell: ({ job }) => job,
                textAlign: 'left',
            }, {
                title: 'Nafn',
                renderCell: ({ name }) => name,
                textAlign: 'left',
            }, {
                title: 'Kennitala',
                renderCell: ({ ssn }) => insertDash(ssn),
                textAlign: 'left',
            }]}
        />
    );
};


// ═══════════════════════════════════════════════════════════════════════════════
// V2-ONLY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════


/**
 * PoliticalPersonList — renders politically exposed persons as inline text
 * in the V2 layout. Only displays when the user answered "Yes".
 */
interface PoliticalPersonListProps {
    question: AnswerDto;
}

/**
 * Formats a single political person entry for display.
 * Format: "SSN, Name, Job" or "Name, Job" if no SSN.
 */
const formatPoliticalPerson = (person: PoliticalDanger): string => {
    const job = person.job ?? '-';
    const name = person.name ?? '-';

    if (!person.ssn) {
        return `${name}, ${job}`;
    }

    const ssn = insertDash(person.ssn);
    return `${ssn}, ${name}, ${job}`;
};

const PoliticalPersonList: React.FC<PoliticalPersonListProps> = ({ question }) => {
    if (question.answerBoolean === false) {
        return null;
    }

    return (
        <div className="politicalPersonWrapper">
            {question.politicalDanger && question.politicalDanger.length > 0 &&
                question.politicalDanger.map((person, index) => (
                    <p key={index} className="paragraph-small--bold">
                        {formatPoliticalPerson(person)}
                    </p>
                ))
            }
        </div>
    );
};


/**
 * HighRiskCountriesAnswer — renders the V2 answer for the high-risk
 * countries question, which stores a JSON array of country entries.
 * Each entry has an Alpha2Code and a Description.
 */
interface HighRiskCountryEntry {
    Alpha2Code: string;
    Description: string;
}

interface HighRiskCountriesAnswerProps {
    answerText: string;
    needsIndent: boolean;
}

const HighRiskCountriesAnswer: React.FC<HighRiskCountriesAnswerProps> = ({ answerText, needsIndent }) => {
    let parsedEntries: { country: string; description: string }[] = [];

    try {
        const parsed: unknown = JSON.parse(answerText);
        if (!Array.isArray(parsed)) return null;

        parsedEntries = parsed
            .filter((entry): entry is HighRiskCountryEntry =>
                typeof entry?.Alpha2Code === 'string' && typeof entry?.Description === 'string'
            )
            .map(entry => ({
                country: entry.Alpha2Code,
                description: entry.Description,
            }));
    } catch {
        return null;
    }

    return (
        <div className="question-row">
            {parsedEntries.map((entry, index) => {
                const countryLabel = CountryList.find(c => c.value === entry.country)?.label;

                return (
                    <React.Fragment key={index}>
                        {/* Country label row */}
                        <div className="question-row__question">
                            <p className="paragraph-small--italic question-description">
                                <span className="paragraph-small">Tilgreinið land:</span>
                            </p>
                        </div>
                        <div className={cx("question-row__answer", {
                            "question-row__answer--indent": needsIndent,
                            "questionIsNotWrong": needsIndent
                        })}>
                            <p className="paragraph-small--bold answer-text">
                                {countryLabel ?? entry.country}
                            </p>
                        </div>

                        {/* Description/explanation row */}
                        <div className="question-row__question">
                            <p className="paragraph-small--italic question-description">
                                <span className="paragraph-small">Vinsamlegast útskýrið nánar:</span>
                            </p>
                        </div>
                        <div className={cx("question-row__answer", {
                            "question-row__answer--indent": needsIndent,
                            "questionIsNotWrong": needsIndent
                        })}>
                            <p className="paragraph-small--bold answer-text">
                                {entry.description}
                            </p>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};


// ═══════════════════════════════════════════════════════════════════════════════
// SHARED COMPONENTS (used by both V1 and V2)
// ═══════════════════════════════════════════════════════════════════════════════


/**
 * ExpandableRegistryInfo — expandable panel showing company registry
 * information. Used for beneficial owners and company registry questions
 * in both V1 and V2 layouts.
 */
interface ExpandableRegistryInfoProps {
    index: number;
    data?: KeldanKYCReport | null;
    questionOrder: QuestionOrderMap;
}

/** Row shape for the company registry (Fyrirtækjaskrá) tables */
interface FyrirtaekjaskraRow {
    kennitala: string;
    nafn: string;
    heimilisfang: string;
    stada?: string;
}

/**
 * Maps raw registry data fields into the standardized table row format.
 * Defined outside the component as a pure function with no state dependencies.
 */
const mapToRegistryRow = (
    kennitala: string,
    nafn: string,
    heimilisfang: string,
    stada?: string
): FyrirtaekjaskraRow => {
    return { kennitala, nafn, heimilisfang, stada };
};

const ExpandableRegistryInfo: React.FC<ExpandableRegistryInfoProps> = ({ index, data, questionOrder }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Base columns shared by all registry tables
    const baseColumns: IColumn<FyrirtaekjaskraRow>[] = [{
        title: 'Nafn',
        renderCell: ({ nafn }) => nafn,
        textAlign: 'left',
    }, {
        title: 'Kennitala',
        renderCell: ({ kennitala }) => insertDash(kennitala),
        textAlign: 'left',
    }, {
        title: 'Heimilisfang',
        renderCell: ({ heimilisfang }) => heimilisfang,
        textAlign: 'left',
    }];

    // Status column for company registry tables (looks up status from adilarfelags data)
    const statusColumn: IColumn<FyrirtaekjaskraRow> = {
        title: 'Staða',
        renderCell: ({ kennitala }) => {
            const adili = data?.adilarfelags?.find(a => a.kennadila === kennitala);
            if (adili === undefined) return '-';
            else if (adili.stodulys) return adili.stodulys;
            else if (adili.teglysing) return adili.teglysing;
            return '-';
        },
        textAlign: 'left'
    };

    const buttonText = index === questionOrder.COMPANY_REGISTRY
        ? "Skoða upplýsingar úr fyrirtækjaskrá"
        : "Skoða raunverulega eigendur";

    const handleToggle = () => setIsOpen(!isOpen);

    return (
        <div
            onClick={() => !isOpen && setIsOpen(true)}
            className={cx("extra-answer-option", { 'open': isOpen })}
        >
            <div className="extra-answer-option-info">
                <span
                    className={cx("extra-answer-option-info-text", { 'open': isOpen })}
                    onClick={handleToggle}
                >
                    {buttonText}
                </span>
                <FontAwesomeIcon
                    icon={faAngleDown}
                    className={cx("fa-icon", { 'open': isOpen })}
                    onClick={handleToggle}
                />
            </div>

            <AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
                <div className="extra-answer-option-tables">
                    {/* Company registry tables (Fyrirtækjaskrá) */}
                    {index === questionOrder.COMPANY_REGISTRY && (
                        <>
                            <div>
                                <h5>Stjórn félags</h5>
                                <Table
                                    tableSize="lg"
                                    showEmptyTableText
                                    data={data?.stjorn.map(item => mapToRegistryRow(item.kennitala, item.nafn, item.heimili, item.tegRec)) ?? []}
                                    columns={[...baseColumns, statusColumn]}
                                />
                            </div>
                            <div>
                                <h5>Endurskoðendur</h5>
                                <Table
                                    tableSize="lg"
                                    showEmptyTableText
                                    data={data?.endurskodendur.map(item => mapToRegistryRow(item.kennitala, item.nafn, item.heimili)) ?? []}
                                    columns={baseColumns}
                                />
                            </div>
                            <div>
                                <h5>Framkvæmdastjórn</h5>
                                <Table
                                    tableSize="lg"
                                    showEmptyTableText
                                    data={data?.framkvaemdarstjorar.map(item => mapToRegistryRow(item.kennitala, item.nafn, item.heimili)) ?? []}
                                    columns={baseColumns}
                                />
                            </div>
                            <div>
                                <h5>Prókúruumboð</h5>
                                <Table
                                    tableSize="lg"
                                    showEmptyTableText
                                    data={data?.prokuruhafar.map(item => mapToRegistryRow(item.kennitala, item.nafn, item.heimili)) ?? []}
                                    columns={baseColumns}
                                />
                            </div>
                            <div>
                                <h5>Stofnendur</h5>
                                <Table
                                    tableSize="lg"
                                    showEmptyTableText
                                    data={data?.stofnendur.map(item => mapToRegistryRow(item.kennitala, item.nafn, item.heimili)) ?? []}
                                    columns={baseColumns}
                                />
                            </div>
                            <div>
                                <h6>Tilgangur</h6>
                                <p className="paragraph-small">{data?.hlutafelag?.tilgangur ?? '-'}</p>
                            </div>
                            <div>
                                <h6>ÍSAT flokkun</h6>
                                <p className="paragraph-small">
                                    {data?.isatfelags && data?.isatfelags.length > 0
                                        ? `${data?.isatfelags[0].isatkodi} - ${data?.isatfelags[0].lysing}`
                                        : '-'}
                                </p>
                            </div>
                        </>
                    )}

                    {/* Beneficial owners table */}
                    {index === questionOrder.BENEFICIAL_OWNERS && (
                        <Table
                            showEmptyTableText
                            tableSize="lg"
                            data={data?.owners ?? []}
                            columns={[{
                                title: 'Nafn',
                                renderCell: ({ nafn }) => nafn,
                                textAlign: 'left',
                            }, {
                                title: 'Fæðingarár/mán.',
                                renderCell: ({ faedingarmanudur }) => faedingarmanudur ? faedingarmanudur.replace('-', '/') : '-',
                                textAlign: 'left',
                            }, {
                                title: 'Eignarhlutur',
                                renderCell: ({ eignarhlutur }) => eignarhlutur ? eignarhlutur : '-',
                            }]}
                        />
                    )}
                </div>
            </AnimateHeight>
        </div>
    );
};
